import NextImage from 'next/image';
import React from 'react';
import type { Asset } from 'contentful';

interface CFImageProps {
  asset: Asset;
  className?: string;
  style?: React.CSSProperties;
  sizes?: string;
  priority?: boolean;
  fill?: boolean;
  decorative?: boolean;
  /** Skip Next.js image optimization — avoids Safari/WebKit black-box rendering with some remote assets. */
  unoptimized?: boolean;
}

export default function CFImage({
  asset,
  className,
  style,
  sizes,
  priority,
  fill,
  decorative = false,
  unoptimized = false,
}: CFImageProps) {
  const { file, title, description } = asset.fields as {
    file: {
      url: string;
      contentType?: string;
      details: { image?: { width: number; height: number } };
    };
    title?: string;
    description?: string;
  };

  const url = file.url.startsWith('//') ? `https:${file.url}` : file.url;
  const isSvg = file.contentType === 'image/svg+xml' || url.toLowerCase().endsWith('.svg');
  const width = file.details.image?.width ?? 1200;
  const height = file.details.image?.height ?? 800;

  let alt: string;
  const isDecorativeResult = decorative;
  if (decorative) {
    alt = '';
  } else {
    alt = (description as string) || (title as string) || '';
    if (!alt && process.env.NODE_ENV === 'development') {
      console.warn(`[CFImage] Missing alt text for image: ${url} — Add a description or title in Contentful to fix this accessibility issue.`);
    }
  }
  const decorativeProps = isDecorativeResult ? { role: 'presentation' as const, 'aria-hidden': true as const } : {};

  if (isSvg) {
    if (fill) {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={url}
          alt={alt}
          className={`absolute inset-0 h-full w-full object-cover ${className ?? ''}`}
          {...decorativeProps}
        />
      );
    }
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={url} alt={alt} className={className} style={style} {...decorativeProps} />
    );
  }

  if (fill) {
    return (
      <NextImage
        src={url}
        alt={alt}
        fill
        className={className}
        sizes={sizes ?? '100vw'}
        priority={priority}
        unoptimized={unoptimized}
        {...decorativeProps}
      />
    );
  }

  return (
    <NextImage
      src={url}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={style}
      sizes={sizes}
      priority={priority}
      unoptimized={unoptimized}
      {...decorativeProps}
    />
  );
}
